/**
 * Contribution path body of `BankTransactionClassificationPanel`.
 * Renders:
 *   - Contact picker (prefilled from BSW-005 suggestion)
 *   - Contribution type dropdown (Individual / Corporate / PAC / Party /
 *     In-Kind / Federal Transfer when ruleset supports it)
 *   - Election designation dropdown
 *   - Payment method dropdown (prefilled to Check when BSW-005 suggests)
 *   - Reference number (prefilled with the check-pattern capture group)
 *   - Notes textarea
 *   - InlineContactForm under the picker (Individual / Organization with a
 *     required subtype; threshold-gated employer / occupation fields)
 *   - GateStack (renders the BSW-002 / CRE-009 rule outcomes; per-gate
 *     override unlocks Classify)
 */
import { Plus } from 'lucide-react';
import { ContactType, ReceiptNature, FinanceFlagSeverity, InKindNature, IN_KIND_NATURE_OPTIONS, PaymentMethod, getGates, requiresInKindNature } from '@signalvote/shared';
import type {
  ComplianceProfileDto,
  CreateFinanceContactRequest,
  FinanceContactDto,
  FinanceSettingsDto,
  RuleOutcomeDto,
} from '@signalvote/shared';
import {
  ContactCombobox,
  GateStack,
  SelectedContactAddressEditor,
} from '@/components/finance/shared/classification';
import { InlineContactForm, TransferQualificationField } from '@/components/finance/entry/entryParts';
import type { ContactAddressPatch } from '@/components/finance/shared/classification';
import { useElectionDesignationOptions, electionOptionKey } from '@/hooks/useElectionDesignationOptions';
import type { UseBankTransactionClassificationFormResult } from './useBankTransactionClassificationForm';

interface ContributionPathBodyProps {
  form: UseBankTransactionClassificationFormResult;
  contacts: FinanceContactDto[];
  /** People who have not given, offered when a search matches no one in `contacts`. */
  prospects?: FinanceContactDto[];
  onContactSearchChange?: (search: string) => void;
  settings: FinanceSettingsDto | null | undefined;
  profile: ComplianceProfileDto | null | undefined;
  rowAmount: number;
  gateOutcomes: RuleOutcomeDto[];
  isClassifying: boolean;
  onClassifyWithOverrides: () => void;
  isCreatingContact: boolean;
  onQuickCreateSave: (request: CreateFinanceContactRequest) => Promise<void>;
  suggestedContactId?: string | null;
  isSavingContactAddress: boolean;
  onSaveContactAddress: (
    contactId: string,
    concurrencyVersion: number,
    patch: ContactAddressPatch,
  ) => void | Promise<void>;
}

export function ContributionPathBody({
  form,
  contacts,
  prospects,
  onContactSearchChange,
  settings,
  profile,
  rowAmount,
  gateOutcomes,
  isClassifying,
  onClassifyWithOverrides,
  isCreatingContact,
  onQuickCreateSave,
  suggestedContactId,
  isSavingContactAddress,
  onSaveContactAddress,
}: ContributionPathBodyProps) {
  const {
    contactId, setContactId,
    contributionType, setContributionType,
    electionDesignation, setElectionDesignation,
    electionYear, setElectionYear,
    paymentMethod, setPaymentMethod,
    referenceNumber, setReferenceNumber,
    inKindFairMarketValueBasis, setInKindFairMarketValueBasis,
    inKindNature, setInKindNature,
    transferQualification, setTransferQualification,
    notes, setNotes,
    showQuickCreate, openQuickCreate, cancelQuickCreate,
    overriddenRuleCodes, addOverride,
  } = form;

  // #144 — the SAME shared hook the Add-Contribution picker uses, so the two dropdowns can't
  // drift: own cycle + next cycle on federal, today's plain list on AL/VA.
  const electionOptions = useElectionDesignationOptions(settings, profile);

  // Contribution type options — additive when the ruleset allows FederalTransfer (a
  // treasurer-friendly hide-when-irrelevant). ADR-057 (#344) — the person/organization gift
  // members were retired; a plain monetary gift is Cash and the contributor's person-vs-org
  // identity lives on the contact's ContactKind/OrganizationType.
  const contributionTypeOptions: { value: ReceiptNature; label: string }[] = [
    { value: ReceiptNature.Cash, label: 'Cash' },
    { value: ReceiptNature.InKind, label: 'In-Kind' },
    // #711 — the jurisdiction's DECLARED capability. Previously gated on `federalTransferLimit
    // != null`, a field the serve path never populated, so the option never rendered anywhere.
    ...(profile?.offersFederalTransferReceipt === true
      ? [{ value: ReceiptNature.FederalTransfer, label: 'Federal Transfer' }]
      : []),
  ];

  // Itemization threshold: Pre-design-doc § 4 — when the row's amount is
  // at or above the configured threshold, address / employer / occupation
  // become required to submit. State-filer copy (per
  // feedback_state_filer_default_not_fec.md) — never "Schedule A" /
  // "per FEC rules" framing in the picker.
  const itemizationThreshold = profile?.itemizationThreshold ?? 0;
  const overItemizationThreshold = Math.abs(rowAmount) >= itemizationThreshold;
  const needsEmployerInfo = profile?.employerOccupationRequired === true;
  const selectedContact = contacts.find((c) => c.id === contactId);

  const isInKind = contributionType === ReceiptNature.InKind;
  // The in-kind nature is an Alabama-only classifier (required by AL EDI; every other
  // state files free-text inKindDescription only and never consumes the value). Route
  // through the shared `requiresInKindNature` predicate — the one source of truth — so
  // the picker hides off-AL rather than inviting a stored-but-never-filed value.
  const inKindNatureRequired = requiresInKindNature(profile?.stateCode);

  const activeGates = gateOutcomes.filter((g) => g.severity === FinanceFlagSeverity.Red || g.severity === FinanceFlagSeverity.Yellow);
  const blockingGates = activeGates.filter((g) => !overriddenRuleCodes.has(g.ruleCode));
  const hasGates = activeGates.length > 0;
  const allGatesOverridden = hasGates && activeGates.every((g) => overriddenRuleCodes.has(g.ruleCode));

  return (
    <div
      data-testid="contribution-path-body"
      className="flex-1 space-y-3 overflow-y-auto mb-4"
    >
      <div>
        <label htmlFor="bt-classify-contact" className="block text-sm font-medium mb-1">
          Contact *
        </label>
        <ContactCombobox
          id="bt-classify-contact"
          value={contactId}
          onChange={setContactId}
          contacts={contacts}
          prospects={prospects}
          onSearchChange={onContactSearchChange}
          placeholder="Select a contact..."
          suggestedContactId={suggestedContactId}
        />
        {!showQuickCreate && (
          <button
            type="button"
            onClick={openQuickCreate}
            className="mt-1 flex items-center gap-1 text-xs text-primary underline"
          >
            <Plus className="h-3 w-3" />
            Create new contact
          </button>
        )}
        {showQuickCreate && (
          <div className="mt-2">
            <InlineContactForm
              contactType={ContactType.Donor}
              showEmployerOccupation={needsEmployerInfo && overItemizationThreshold}
              supportedOrganizationTypes={profile?.supportedOrganizationTypes ?? []}
              addressAutocomplete
              isSaving={isCreatingContact}
              onSave={onQuickCreateSave}
              onCancel={cancelQuickCreate}
            />
          </div>
        )}
        {!showQuickCreate && selectedContact && (
          <SelectedContactAddressEditor
            key={selectedContact.id}
            contact={selectedContact}
            noun="donor"
            addressMattersForFiling={overItemizationThreshold}
            isSaving={isSavingContactAddress}
            onSaveAddress={(patch) =>
              onSaveContactAddress(
                selectedContact.id,
                selectedContact.concurrencyVersion,
                patch,
              )
            }
          />
        )}
      </div>

      <div>
        <label htmlFor="bt-classify-type" className="block text-sm font-medium mb-1">
          Contribution type *
        </label>
        <select
          id="bt-classify-type"
          value={contributionType}
          onChange={(e) => setContributionType(e.target.value as ReceiptNature)}
          className="w-full rounded-md border px-3 py-2 text-sm"
        >
          {contributionTypeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* #711 — a transfer only skips the contribution limits when it came from another committee
          authorized by the same candidate. Asked here as well as on manual entry so a classified
          bank row cannot take the exemption a manually-entered one has to earn. */}
      {contributionType === ReceiptNature.FederalTransfer && (
        <TransferQualificationField
          qualification={transferQualification}
          onChange={setTransferQualification}
          idPrefix="bt-classify-transfer-qualification"
        />
      )}

      {isInKind && (
        <div>
          <label
            htmlFor="bt-classify-fmv"
            className="block text-sm font-medium mb-1"
          >
            Fair market value basis *
          </label>
          <input
            id="bt-classify-fmv"
            type="text"
            value={inKindFairMarketValueBasis}
            onChange={(e) => setInKindFairMarketValueBasis(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm"
            placeholder="e.g., Appraised value of donated office furniture"
          />
        </div>
      )}

      {isInKind && inKindNatureRequired && (
        <div>
          <label
            htmlFor="bt-classify-inkind-nature"
            className="block text-sm font-medium mb-1"
          >
            In-kind nature *
          </label>
          <select
            id="bt-classify-inkind-nature"
            value={inKindNature ?? ''}
            onChange={(e) => setInKindNature(e.target.value ? (e.target.value as InKindNature) : null)}
            className="w-full rounded-md border px-3 py-2 text-sm"
          >
            <option value="">Select a nature...</option>
            {IN_KIND_NATURE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}

      {electionOptions.length > 0 && (
        <div>
          <label
            htmlFor="bt-classify-election"
            className="block text-sm font-medium mb-1"
          >
            Election designation
          </label>
          <select
            id="bt-classify-election"
            value={electionOptionKey(electionDesignation, electionYear)}
            onChange={(e) => {
              const opt = electionOptions.find((o) => o.key === e.target.value);
              if (opt) {
                setElectionDesignation(opt.designation);
                setElectionYear(opt.electionYear);
              }
            }}
            className="w-full rounded-md border px-3 py-2 text-sm"
          >
            {electionOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label
          htmlFor="bt-classify-payment-method"
          className="block text-sm font-medium mb-1"
        >
          Payment method
        </label>
        <select
          id="bt-classify-payment-method"
          value={paymentMethod ?? ''}
          onChange={(e) =>
            setPaymentMethod(e.target.value ? (e.target.value as PaymentMethod) : null)
          }
          className="w-full rounded-md border px-3 py-2 text-sm"
        >
          <option value="">Not specified</option>
          {Object.values(PaymentMethod).map((pm) => (
            <option key={pm} value={pm}>
              {pm === 'CreditCard' ? 'Credit Card' : pm === 'InKind' ? 'In-Kind' : pm}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor="bt-classify-reference-number"
          className="block text-sm font-medium mb-1"
        >
          Reference number
        </label>
        <input
          id="bt-classify-reference-number"
          type="text"
          value={referenceNumber}
          onChange={(e) => setReferenceNumber(e.target.value)}
          className="w-full rounded-md border px-3 py-2 text-sm"
          placeholder="e.g., Check #1234"
        />
      </div>

      <div>
        <label
          htmlFor="bt-classify-notes"
          className="block text-sm font-medium mb-1"
        >
          Notes
        </label>
        <textarea
          id="bt-classify-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full rounded-md border px-3 py-2 text-sm"
          rows={2}
        />
      </div>

      {hasGates && (
        <GateStack
          gateOutcomes={activeGates}
          overriddenRuleCodes={overriddenRuleCodes as Set<string>}
          allGatesOverridden={allGatesOverridden}
          isClassifying={isClassifying}
          onOverride={addOverride}
          onClassifyWithOverrides={onClassifyWithOverrides}
        />
      )}

      {/* expose blocking count for tests / future overflow rendering */}
      <input type="hidden" data-testid="blocking-gate-count" value={blockingGates.length} readOnly />
    </div>
  );
}

/**
 * BSW-008: convenience — pull the gate-shaped outcomes out of a
 * validate-draft response. Sibling helper to the shared `getGates` but
 * with a stricter return type (the legacy shared util returns any
 * severity; the panel only cares about gating ones).
 */
export function gatesFromOutcomes(outcomes: RuleOutcomeDto[]): RuleOutcomeDto[] {
  return getGates(outcomes);
}
